import { createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { vulnerabilitiesApi } from "../report/services/vulnerabilities.api";
import { initializeState, reportCleared } from "./sdlcReportSlice";
import { restoreState } from "./utils";
/** Initial state for vulnerabilities table filter */
export const initFilter = {
    cve_number: "",
    ui_item_id: null,
    ui_selected_name: null,
    dependency_id: null,
    reference_id: null,
    exploit_type: [],
    cvss_score_severity: "ALL",
    hide_triaged: true,
};
/** Initial state for vulnerabilities table sort */
export const initSort = {
    property: "cvss",
    reverse: true,
};
/** Initial state for vulnerabilities table pagination */
export const initPagination = {
    currentPage: 1,
    itemsPerPage: 10,
};
export const initialState = {
    filter: initFilter,
    sort: initSort,
    pagination: initPagination,
    expandedRows: {},
    focusedRow: null,
    filteredByLink: false,
};
export const vulnerabilitySlice = createSlice({
    name: "vulnerability",
    initialState: initialState,
    reducers: {
        filterUpdated: (state, action) => {
            state.filter = cloneDeep(action.payload);
            state.filter.ui_item_id = null;
            state.filteredByLink = false;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        cveFiltered: (state, action) => {
            state.filter = cloneDeep(initialState.filter);
            state.filter.cve_number = action.payload;
            state.filter.ui_item_id = action.payload;
            state.filter.hide_triaged = false;
            state.filteredByLink = true;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        severityFiltered: (state, action) => {
            state.filter = cloneDeep(initialState.filter);
            state.filter.cvss_score_severity = action.payload;
            state.filteredByLink = true;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        sourceFiltered: (state, action) => {
            state.filter = cloneDeep(initialState.filter);
            state.filter.ui_selected_name = action.payload.name;
            state.filteredByLink = true;
            if (action.payload.type === "COMPONENT")
                state.filter.reference_id = action.payload.id;
            else if (action.payload.type === "DEPENDENCY")
                state.filter.dependency_id = action.payload.id;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        tagFiltered: (state, action) => {
            state.filter = cloneDeep(initialState.filter);
            state.filter.exploit_type = action.payload;
            state.filteredByLink = true;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        dependencyTagFiltered: (state, action) => {
            state.filter = cloneDeep(initialState.filter);
            state.filter.ui_selected_name = action.payload.name;
            state.filter.dependency_id = action.payload.id;
            state.filter.exploit_type = action.payload.exploit_type;
            state.filteredByLink = true;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        resetFilter: (state) => {
            state.filter = cloneDeep(initialState.filter);
            state.filteredByLink = false;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        sortUpdated: (state, action) => {
            state.sort = action.payload;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        paginationUpdated: (state, action) => {
            state.pagination = action.payload;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        focusedRowUpdated: (state, action) => {
            state.focusedRow = action.payload;
        },
        expandedRowsUpdated: (state, action) => {
            if (action.payload.expand) {
                state.expandedRows[action.payload.id] = initPagination;
            }
            else {
                delete state.expandedRows[action.payload.id];
            }
        },
        expandedRowPaginationUpdated: (state, action) => {
            state.expandedRows[action.payload.id] = action.payload.pagination;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initializeState, (state, action) => {
            return restoreState(state, action.payload, "vulnerabilities");
        });
        builder.addCase(reportCleared, () => {
            return cloneDeep(initialState);
        });
        builder.addMatcher(vulnerabilitiesApi.endpoints.getVulnerabilities.matchFulfilled, (state, action) => {
            if (!state.filteredByLink || action.payload.match_count !== 1)
                return;
            state.expandedRows[action.payload.vulnerabilities[0].cve_number] =
                initPagination;
            state.focusedRow = action.payload.vulnerabilities[0].cve_number;
        });
    },
});
export const { filterUpdated, cveFiltered, severityFiltered, sourceFiltered, tagFiltered, dependencyTagFiltered, resetFilter, sortUpdated, paginationUpdated, expandedRowsUpdated, expandedRowPaginationUpdated, focusedRowUpdated, } = vulnerabilitySlice.actions;
export const selectFilter = (state) => state.report.vulnerabilities.filter;
export const selectReferenceName = (state) => state.report.vulnerabilities.filter.ui_selected_name;
export const selectSort = (state) => state.report.vulnerabilities.sort;
export const selectPagination = (state) => state.report.vulnerabilities.pagination;
export const selectExpandedRow = (state, id) => state.report.vulnerabilities.expandedRows[id] !== undefined;
export const selectExpandedRowPagination = (state, id) => { var _a; return (_a = state.report.vulnerabilities.expandedRows[id]) !== null && _a !== void 0 ? _a : initPagination; };
export const selectFocusedRow = (state) => state.report.vulnerabilities.focusedRow;
export const selectFilteredByLink = (state) => state.report.vulnerabilities.filteredByLink;
export const vulnerabilityReducer = vulnerabilitySlice.reducer;
export * as default from "./vulnerabilitySlice";
//# sourceMappingURL=vulnerabilitySlice.js.map